import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { HERO_IMAGE_COUNT } from '~/components/Bedbank/Properties/Curation/constants';
import type {
  CurationClassification,
  CurationItem,
  CurationStatus,
  MutateReviewDecisions,
  ReviewDecisions,
} from '~/components/Bedbank/Properties/Curation/types';

import BedbankService from '~/services/BedbankService';

export const curationQueryKeys = {
  all: ['curation'] as const,
  lists: () => [...curationQueryKeys.all, 'list'] as const,
  list: (filters: {
    page: number;
    status?: CurationStatus | '';
    classification?: CurationClassification | '';
    reviewDecision?: ReviewDecisions | '';
  }) => [...curationQueryKeys.lists(), filters] as const,
  details: () => [...curationQueryKeys.all, 'detail'] as const,
  detail: (propertyId: string) => [...curationQueryKeys.details(), propertyId] as const,
};

interface CurationListFilters {
  page: number;
  pageSize: number;
  status: CurationStatus | '';
  classification: CurationClassification | '';
  reviewDecision: ReviewDecisions | '';
}

export function useGetCurationList({ page, pageSize, status, classification, reviewDecision }: CurationListFilters) {
  return useQuery({
    queryKey: curationQueryKeys.list({ page, status, classification, reviewDecision }),
    queryFn: async ({ signal }) => {
      const params: Record<string, unknown> = {
        page: page + 1,
        limit: pageSize,
      };
      if (status) {
        params.status = status;
      }
      if (classification) {
        params.classification = classification;
      }
      if (reviewDecision) {
        params.reviewDecision = reviewDecision;
      }
      const response = await BedbankService.getCurationList(params);
      return { result: response.result as Array<CurationItem>, count: response.count as number };
    },
  });
}

export function useGetCurationDetail(propertyId: string) {
  return useQuery({
    queryKey: curationQueryKeys.detail(propertyId),
    queryFn: async () => {
      const response = await BedbankService.getCurationDetail(propertyId);
      return response.result as CurationItem;
    },
    enabled: !!propertyId,
  });
}

export function useCurateProperties() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (propertyIds: Array<string>) => BedbankService.curateProperties(propertyIds),
    onSuccess: ({ result }) => {
      enqueueSnackbar(`Queued ${result.queued} properties for curation`, { variant: 'success' });
    },
    onError: (err: Error) => {
      enqueueSnackbar(`Curation failed: ${err.message}`, { variant: 'error' });
    },
  });
}

export function useReviewCuration() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ propertyId, decision }: { propertyId: string; decision: MutateReviewDecisions }) =>
      BedbankService.reviewCuration(propertyId, decision),
    onSuccess: (_data, variables) => {
      const label = variables.decision === 'approved' ? 'approved and set live' : 'rejected';
      enqueueSnackbar(`Property ${label}`, { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: curationQueryKeys.all });
    },
    onError: (err: Error, variables) => {
      const action = variables.decision === 'approved' ? 'approve' : 'reject';
      enqueueSnackbar(`Failed to ${action}: ${err.message}`, { variant: 'error' });
    },
  });
}

export function useBulkReviewCuration() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ propertyIds, decision }: { propertyIds: string[]; decision: MutateReviewDecisions }) =>
      BedbankService.bulkReviewCuration(propertyIds, decision),
    onSuccess: (_data, variables) => {
      const label = variables.decision === 'approved' ? 'approved and set live' : 'rejected';
      enqueueSnackbar(`Property ${label}`, { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: curationQueryKeys.all });
    },
    onError: (err: Error, variables) => {
      const action = variables.decision === 'approved' ? 'approve' : 'reject';
      enqueueSnackbar(`Failed to ${action}: ${err.message}`, { variant: 'error' });
    },
  });
}

// Whitelist fields for the PATCH payload — API response images may contain extra fields
function mapImageForApi(img: {
  id: string;
  hidden: boolean;
  order: number;
  title: string;
  is_hero: boolean;
  is_deleted: boolean;
  source: string;
}) {
  return {
    id: img.id,
    hidden: img.hidden,
    order: img.order,
    title: img.title,
    is_hero: img.is_hero,
    is_deleted: img.is_deleted,
    source: img.source,
  };
}

export function useReorderCurationImages() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      propertyId,
      orderedImageServiceIds,
    }: {
      propertyId: string;
      orderedImageServiceIds: Array<string>;
    }) => {
      const property = await BedbankService.getPropertyEdit(propertyId);
      const result = property.result;
      const propertyImages = result.images;

      const imageServiceIdToIndex = new Map(orderedImageServiceIds.map((id, i) => [id, i]));

      const reordered = propertyImages.map((img) => {
        const newIndex = imageServiceIdToIndex.get(img.id);
        if (newIndex != null) {
          return { ...img, order: newIndex, is_hero: newIndex < HERO_IMAGE_COUNT };
        }
        return img;
      });

      return BedbankService.patchPropertyEdit(propertyId, {
        images: reordered.map(mapImageForApi),
        updatedAt: result.updatedAt,
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Image order saved', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: curationQueryKeys.all });
    },
    onError: (err: Error) => {
      enqueueSnackbar(`Failed to save image order: ${err.message}`, { variant: 'error' });
    },
  });
}
